/**
 * 🎨 PROFILE SETUP MODAL - Mode Mozart/Jobs/Tesla
 * 
 * Modal de configuration du profil Heartbeats
 * Inclut : Bio, Avatar, Photos, Passions, Mode Couples
 * ✅ Système de récompenses progressives (sons + confettis)
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, Image, X, Check, Sparkles, MapPin, Heart, Users,
  ChevronLeft, ChevronRight, Palette, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '../ui/dialog';
import { toast } from 'sonner';
import { api } from '../../services/api';

// Soul components
import { PassionSelector, SoulQuestionsWizard } from './soul/SoulPassions';
import { RELATIONSHIP_STATUS, COUPLE_INTERESTS } from './CoupleMode';

// Avatar Creator
import AvatarMagiqueCreator from '../AvatarCreator';

// Profile Frames - Import from shared components
import { ProfileFrame, FrameSelector, PROFILE_FRAMES } from './shared/ProfileFrames';
import { HeaderSelector, PROFILE_HEADERS } from './shared/ProfileHeaders';

// Guide Assistant - Pixie/Enzo
import { GuideAssistant, getGuideForGender } from './GuideAssistant';

// 🎵 Système de récompenses progressives
import { useProfileRewards } from '../../hooks/useProfileRewards';

// Confetti pour célébrations manuelles
import confetti from 'canvas-confetti';

// Simple avatars
const SIMPLE_AVATARS = [
  '😊', '😎', '🥰', '😏', '🤩', '😇', '🦊', '🐱', '🦁', '🐻',
  '🌸', '🌺', '🌻', '🌷', '💐', '🌹', '🔥', '⭐', '💫', '🌙',
  '💜', '💖', '❤️‍🔥', '💝', '🦋', '🌈', '✨', '🎭', '🎪', '🎨'
];

function ProfileSetupModal({ isOpen, onClose, onSave, customer, existingProfile, isDarkMode }) {
  // 🔧 FALLBACK: Récupérer customerId même si customer est undefined
  const getCustomerId = () => {
    if (customer?.id) return customer.id;
    // Fallback: essayer de récupérer depuis localStorage
    try {
      const savedUser = localStorage.getItem('heartbeats_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        return parsed.id;
      }
    } catch (e) {}
    // Fallback LPE
    try {
      const savedCustomer = localStorage.getItem('lpep_customer');
      if (savedCustomer) {
        const parsed = JSON.parse(savedCustomer);
        return parsed.id;
      }
    } catch (e) {}
    return null;
  };
  
  const customerId = getCustomerId();
  
  const [bio, setBio] = useState(existingProfile?.bio || '');
  
  // 🎵 Système de récompenses progressives
  const { playReward, checkAndReward } = useProfileRewards();
  
  // 🧚 Contexte actuel pour le guide (détecté automatiquement)
  const [guideContext, setGuideContext] = useState('welcome');
  const [interests, setInterests] = useState(existingProfile?.interests?.join(', ') || '');
  // Utiliser avatarImage (magique) en priorité, sinon avatar classique
  const [avatar, setAvatar] = useState(existingProfile?.avatarImage || existingProfile?.avatar || '😊');
  const [lookingFor, setLookingFor] = useState(existingProfile?.lookingFor || 'all');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [city, setCity] = useState(existingProfile?.city || '');
  const [customCity, setCustomCity] = useState('');
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [showAvatarCreator, setShowAvatarCreator] = useState(false);
  const [showSimpleAvatars, setShowSimpleAvatars] = useState(false);
  const [profileFrame, setProfileFrame] = useState(existingProfile?.profileFrame || 'gradient_purple');
  const [showFrameSelector, setShowFrameSelector] = useState(false);
  const fileInputRef = useRef(null);
  
  // 👤 IDENTITÉ
  const [displayName, setDisplayName] = useState(existingProfile?.displayName || customer?.firstName || '');
  const [lastName, setLastName] = useState(existingProfile?.lastName || customer?.lastName || '');
  const [pseudo, setPseudo] = useState(existingProfile?.pseudo || '');
  
  // 🎨 HEADER PERSONNALISABLE
  const [profileHeader, setProfileHeader] = useState(existingProfile?.profileHeader || 'gradient_purple_pink');
  const [showHeaderSelector, setShowHeaderSelector] = useState(false);
  
  // 📷 Selfie camera
  const [showSelfieCamera, setShowSelfieCamera] = useState(false);
  const selfieVideoRef = useRef(null);
  const selfieCanvasRef = useRef(null);
  const selfieStreamRef = useRef(null);
  
  // 🌌 PASSIONS PROFONDES - Seront gérées dans PassionsSetupModal (étape 2)
  const [passions, setPassions] = useState(existingProfile?.passions || {});
  const [soulAnswers, setSoulAnswers] = useState(existingProfile?.soulAnswers || {});

  // 💕 MODE COUPLES & AMIS - Pour les couples qui cherchent des amis !
  const [relationshipStatus, setRelationshipStatus] = useState(existingProfile?.relationshipStatus || 'single');
  const [partnerName, setPartnerName] = useState(existingProfile?.partnerName || '');
  const [coupleInterests, setCoupleInterests] = useState(existingProfile?.coupleInterests || []);
  const [lookingForFriends, setLookingForFriends] = useState(existingProfile?.lookingForFriends ?? true);

  // Liste des villes prédéfinies
  const KNOWN_CITIES = [
    'Vermelles', 'Noyelles-lès-Vermelles', 'Sailly-Labourse', 'Labourse', 'Annequin',
    'Cambrin', 'Cuinchy', 'Festubert', 'Hulluch', 'Benifontaine', 'Mazingarbe',
    'Grenay', 'Bully-les-Mines', 'Aix-Noulette', 'Haisnes', 'Auchy-les-Mines',
    'Loos-en-Gohelle', 'Noeux-les-Mines', 'Béthune', 'Lens', 'Liévin',
    'La Bassée', 'Carvin', 'Hénin-Beaumont', 'Bruay-la-Buissière'
  ];

  // Vérifier si la ville existante est une ville personnalisée
  useEffect(() => {
    if (existingProfile?.city && !KNOWN_CITIES.includes(existingProfile.city)) {
      setShowCustomCity(true);
      setCustomCity(existingProfile.city);
      setCity('other');
    }
  }, [existingProfile?.city]);

  const avatarOptions = ['😊', '😎', '🥰', '😏', '🤗', '😇', '🤩', '😋', '🥳', '😈', '👻', '🦊', '🐱', '🦁', '🐼'];

  // Handler pour sauvegarder l'avatar personnalisé
  const handleAvatarSave = (avatarData) => {
    setAvatar(avatarData);
    setShowAvatarCreator(false);
    // 🎵 Récompense pour avatar IA
    playReward('avatar');
    toast.success('🎨 Avatar créé ! Tu es magnifique ! 💜');
  };

  // Charger les photos existantes
  useEffect(() => {
    if (customerId && isOpen) {
      loadPhotos();
    }
  }, [customerId, isOpen]);

  const loadPhotos = async () => {
    if (!customerId) return;
    try {
      const result = await api.getProfilePhotos(customerId, true);
      setPhotos(result.photos || []);
    } catch (error) {
      console.error('Erreur chargement photos:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!customerId) {
      toast.error('Erreur: veuillez vous reconnecter');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont acceptées');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }

    if (photos.length >= 5) {
      toast.error('Maximum 5 photos autorisées');
      return;
    }

    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64 = event.target.result;
          await api.uploadProfilePhoto(customerId, base64);
          playReward('photo');
          toast.success('📸 Photo uploadée avec succès !');
          loadPhotos();
        } catch (error) {
          console.error('Erreur upload photo:', error);
          toast.error('Erreur lors de l\'upload');
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur');
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!customerId) return;
    try {
      await api.deleteProfilePhoto(customerId, photoId);
      toast.success('Photo supprimée');
      loadPhotos();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSetMainPhoto = async (photoId) => {
    if (!customerId) return;
    try {
      await api.setMainPhoto(customerId, photoId);
      toast.success('Photo principale mise à jour');
      loadPhotos();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // 📷 SELFIE FUNCTIONS
  const startSelfieCamera = async () => {
    // Vérifier si l'API est disponible
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('📷 Caméra non supportée sur ce navigateur. Essayez Chrome ou Safari.', { duration: 5000 });
      return;
    }
    
    // Vérifier si on est en HTTPS (requis pour la caméra)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      toast.error('📷 La caméra nécessite une connexion sécurisée (HTTPS).', { duration: 5000 });
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } }, 
        audio: false 
      });
      selfieStreamRef.current = stream;
      if (selfieVideoRef.current) {
        selfieVideoRef.current.srcObject = stream;
      }
      setShowSelfieCamera(true);
    } catch (error) {
      console.error('Erreur caméra:', error);
      
      // Messages d'erreur spécifiques selon le type d'erreur
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('📷 Accès à la caméra refusé. Autorisez l\'accès dans les paramètres de votre navigateur.', { duration: 6000 });
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('📷 Aucune caméra détectée sur cet appareil. Utilisez l\'import de photo à la place.', { duration: 5000 });
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('📷 La caméra est utilisée par une autre application. Fermez-la et réessayez.', { duration: 5000 });
      } else if (error.name === 'OverconstrainedError') {
        // Réessayer avec des contraintes moins strictes
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ 
            video: true, 
            audio: false 
          });
          selfieStreamRef.current = fallbackStream;
          if (selfieVideoRef.current) {
            selfieVideoRef.current.srcObject = fallbackStream;
          }
          setShowSelfieCamera(true);
          return;
        } catch (fallbackError) {
          toast.error('📷 Impossible d\'accéder à la caméra.', { duration: 5000 });
        }
      } else {
        toast.error(`📷 Erreur caméra: ${error.message || 'Vérifiez vos permissions'}`, { duration: 5000 });
      }
    }
  };

  const takeSelfie = async () => {
    if (!selfieVideoRef.current || !selfieCanvasRef.current) return;
    
    const video = selfieVideoRef.current;
    const canvas = selfieCanvasRef.current;
    canvas.width = 400;
    canvas.height = 400;
    
    const ctx = canvas.getContext('2d');
    
    // Crop carré centré + flip horizontal
    const size = Math.min(video.videoWidth, video.videoHeight);
    const x = (video.videoWidth - size) / 2;
    const y = (video.videoHeight - size) / 2;
    
    ctx.translate(400, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, x, y, size, size, 0, 0, 400, 400);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    
    const photoData = canvas.toDataURL('image/jpeg', 0.85);
    
    // Stop camera
    stopSelfieCamera();
    
    // Upload la photo
    if (photoData && photoData.length > 100 && customerId) {
      setUploadingPhoto(true);
      try {
        await api.uploadProfilePhoto(customerId, photoData);
        toast.success('📸 Selfie uploadé avec succès !');
        loadPhotos();
      } catch (error) {
        toast.error('Erreur lors de l\'upload');
      } finally {
        setUploadingPhoto(false);
      }
    }
  };

  const stopSelfieCamera = () => {
    if (selfieStreamRef.current) {
      selfieStreamRef.current.getTracks().forEach(track => track.stop());
    }
    setShowSelfieCamera(false);
  };

  // Utiliser l'avatar IA comme photo de profil
  const useAvatarAsPhoto = async () => {
    if (!avatar || !avatar.startsWith('data:')) {
      toast.error('Crée d\'abord un avatar magique ! 🎨');
      return;
    }
    
    if (photos.length >= 5) {
      toast.error('Maximum 5 photos atteint');
      return;
    }
    
    if (!customerId) {
      toast.error('Erreur: veuillez vous reconnecter');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      await api.uploadProfilePhoto(customerId, avatar);
      toast.success('🎨 Avatar ajouté aux photos !');
      loadPhotos();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = () => {
    const interestsArray = interests.split(',').map(i => i.trim()).filter(i => i);
    // Utiliser customCity si "other" est sélectionné, sinon utiliser city
    const finalCity = city === 'other' ? customCity : city;
    
    // Déterminer si l'avatar est une image magique (base64) ou un emoji
    const isAvatarMagique = typeof avatar === 'string' && avatar.startsWith('data:');
    
    const profileData = {
      bio,
      interests: interestsArray.slice(0, 5),
      avatar: isAvatarMagique ? '🎨' : avatar,
      avatarImage: isAvatarMagique ? avatar : null,
      profileFrame,
      profileHeader,
      lookingFor,
      city: finalCity,
      displayName: displayName || customer?.firstName || customer?.name?.split(' ')[0] || 'Anonyme',
      lastName: lastName || null,
      pseudo: pseudo || null,
      isVisible: true,
      passions,
      soulAnswers,
      relationshipStatus,
      partnerName: relationshipStatus === 'couple' ? partnerName : null,
      coupleInterests: relationshipStatus !== 'single' ? coupleInterests : [],
      lookingForFriends: relationshipStatus !== 'single' ? lookingForFriends : false,
      photos: photos // Pour le calcul de complétion
    };
    
    // 🎵 Vérifier les récompenses (bio ajoutée, etc.)
    if (bio && bio.length > 10 && (!existingProfile?.bio || existingProfile.bio.length <= 10)) {
      playReward('bio');
    }
    
    // Calculer la complétion pour le feu d'artifice
    const hasAllRequired = bio && bio.length > 10 && 
                          (isAvatarMagique || photos.length > 0) &&
                          Object.values(passions).flat().length >= 5 &&
                          displayName;
    
    if (hasAllRequired && existingProfile && !existingProfile._wasComplete) {
      // 🎆 Profil complet pour la première fois !
      setTimeout(() => playReward('complete'), 500);
    }
    
    onSave(profileData);
  };

  // Trouver la photo principale pour l'aperçu
  const mainPhoto = photos.find(p => p.isMain && p.status === 'approved') || photos.find(p => p.status === 'approved');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`sm:max-w-lg overflow-y-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
        style={{
          maxHeight: 'calc(100dvh - 2rem)',
          maxHeight: 'calc(var(--app-height, 100vh) - 2rem)',
        }}
      >
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
            <Heart className="w-6 h-6 text-pink-500" />
            {existingProfile ? 'Retouche ton profil 🔥' : 'Crée ton profil Heartbeats 💜'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* 🧚 Guide Pixie/Enzo - Banner contextuel */}
          <GuideAssistant
            gender={customer?.gender || 'male'}
            context={guideContext}
            customerName={displayName || customer?.firstName}
            isDarkMode={isDarkMode}
            position="banner"
          />
          
          {/* ═══════════════════════════════════════════════════════════
              📸 SECTION 1 : TA PHOTO - Fais péter ta plus belle tof !
              ═══════════════════════════════════════════════════════════ */}
          <div 
            className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800/70' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            onFocus={() => setGuideContext('photo')}
            onMouseEnter={() => setGuideContext('photo')}
          >
            <h3 className={`font-bold mb-2 flex items-center gap-2 text-center justify-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📸 Fais péter ta plus belle tof !
            </h3>
            <p className={`text-xs mb-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Les vraies photos = 10x plus de matchs ! 🔥
            </p>
            
            {/* Aperçu photo GRAND FORMAT */}
            <div className="flex flex-col items-center mb-4">
              <div className="relative">
                <ProfileFrame
                  src={
                    (photos.length > 0 && photos[0]?.data) 
                      ? photos[0].data
                      : (typeof avatar === 'string' && avatar.startsWith('data:'))
                        ? avatar
                        : null
                  }
                  emoji={(typeof avatar === 'string' && !avatar.startsWith('data:')) ? avatar : null}
                  frameId={profileFrame}
                  size={180}
                />
                {/* Bouton cadre sur la photo */}
                <button
                  onClick={() => setShowFrameSelector(!showFrameSelector)}
                  className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform text-lg"
                  title="Personnaliser le cadre"
                >
                  🖼️
                </button>
              </div>
              <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {photos.length}/5 photos • {PROFILE_FRAMES.find(f => f.id === profileFrame)?.emoji} {PROFILE_FRAMES.find(f => f.id === profileFrame)?.name}
              </p>
            </div>
            
            {/* Sélecteur de cadres (dépliable) */}
            <AnimatePresence>
              {showFrameSelector && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 overflow-hidden"
                >
                  <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                      Parce qu'un simple rond c'est ennuyeux ! 😴
                    </p>
                    <FrameSelector
                      currentFrame={profileFrame}
                      onSelect={(frameId) => {
                        setProfileFrame(frameId);
                        toast.success(`${PROFILE_FRAMES.find(f => f.id === frameId)?.emoji} Cadre appliqué !`);
                      }}
                      isDarkMode={isDarkMode}
                      showPremiumLock={true}
                      userHasPremium={false}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Mode Selfie Camera */}
            {showSelfieCamera && (
              <div className="mb-4 text-center">
                <div className="relative inline-block">
                  <video
                    ref={selfieVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-40 h-40 rounded-xl object-cover border-4 border-pink-500"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  <canvas ref={selfieCanvasRef} className="hidden" />
                </div>
                <div className="flex gap-2 justify-center mt-3">
                  <Button onClick={takeSelfie} className="bg-green-500 hover:bg-green-600">
                    <Camera className="w-4 h-4 mr-1" /> Capturer
                  </Button>
                  <Button onClick={stopSelfieCamera} variant="outline">
                    Annuler
                  </Button>
                </div>
              </div>
            )}
            
            {/* 4 boutons en ligne */}
            {!showSelfieCamera && (
              <div className="grid grid-cols-4 gap-2">
                {/* 1. Selfie */}
                <button
                  onClick={startSelfieCamera}
                  disabled={uploadingPhoto || photos.length >= 5}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                    isDarkMode 
                      ? 'border-gray-600 bg-gray-700/50 hover:border-pink-500 hover:bg-pink-500/10' 
                      : 'border-gray-200 bg-white hover:border-pink-500 hover:bg-pink-50'
                  } ${(uploadingPhoto || photos.length >= 5) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Camera className={`w-6 h-6 mb-1 ${isDarkMode ? 'text-pink-400' : 'text-pink-500'}`} />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Selfie</span>
                </button>
                
                {/* 2. Import Client-Malin - UNIQUEMENT pour utilisateurs LPE */}
                {/* Masqué pour les utilisateurs standalone heartbeats.fr car ils n'ont pas de profil LPE */}
                {customer?.profilePhoto || customer?.firstName || customer?.address ? (
                <button
                  onClick={async () => {
                    let imported = [];
                    setUploadingPhoto(true);
                    
                    try {
                      // 📷 Importer la photo
                      if (customer?.profilePhoto && photos.length < 5 && customerId) {
                        const photoExists = photos.some(p => p.data === customer.profilePhoto);
                        if (!photoExists) {
                          await api.uploadProfilePhoto(customerId, customer.profilePhoto);
                          await loadPhotos();
                          imported.push('photo');
                        }
                      }
                      
                      // 👤 Importer prénom
                      if (customer?.firstName && !displayName) {
                        setDisplayName(customer.firstName);
                        imported.push('prénom');
                      }
                      
                      // 👤 Importer nom (depuis name complet ou lastName)
                      if (!lastName) {
                        if (customer?.lastName) {
                          setLastName(customer.lastName);
                          imported.push('nom');
                        } else if (customer?.name) {
                          const parts = customer.name.split(' ');
                          if (parts.length > 1) {
                            setLastName(parts.slice(1).join(' '));
                            imported.push('nom');
                          }
                        }
                      }
                      
                      // 📍 Importer la ville depuis l'adresse
                      if (customer?.address && !city) {
                        const cityMatch = customer.address.match(/(\d{5})\s+([A-Za-zÀ-ÿ\-\s]+)/);
                        if (cityMatch && cityMatch[2]) {
                          setCity('other');
                          setCustomCity(cityMatch[2].trim());
                          setShowCustomCity(true);
                          imported.push('ville');
                        }
                      }
                      
                      if (imported.length > 0) {
                        toast.success(`📥 Importé : ${imported.join(', ')} ! ✨`);
                        confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 }, colors: ['#8B5CF6', '#EC4899'] });
                      } else {
                        toast.info('Tout est déjà importé ! 👌');
                      }
                    } catch (error) {
                      toast.error('Erreur lors de l\'import');
                    } finally {
                      setUploadingPhoto(false);
                    }
                  }}
                  disabled={uploadingPhoto}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                    isDarkMode 
                      ? 'border-gray-600 bg-gray-700/50 hover:border-purple-500 hover:bg-purple-500/10' 
                      : 'border-gray-200 bg-white hover:border-purple-500 hover:bg-purple-50'
                  } ${uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Zap className={`w-6 h-6 mb-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Import</span>
                </button>
                ) : null}
                
                {/* 3. Avatar Magique */}
                <button
                  onClick={() => setShowAvatarCreator(true)}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                    isDarkMode 
                      ? 'border-gray-600 bg-gray-700/50 hover:border-cyan-500 hover:bg-cyan-500/10' 
                      : 'border-gray-200 bg-white hover:border-cyan-500 hover:bg-cyan-50'
                  }`}
                >
                  <Sparkles className={`w-6 h-6 mb-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Magie</span>
                </button>
                
                {/* 4. Emoji */}
                <button
                  onClick={() => setShowSimpleAvatars(!showSimpleAvatars)}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                    showSimpleAvatars
                      ? isDarkMode 
                        ? 'border-yellow-500 bg-yellow-500/20' 
                        : 'border-yellow-500 bg-yellow-50'
                      : isDarkMode 
                        ? 'border-gray-600 bg-gray-700/50 hover:border-yellow-500 hover:bg-yellow-500/10' 
                        : 'border-gray-200 bg-white hover:border-yellow-500 hover:bg-yellow-50'
                  }`}
                >
                  <span className="text-2xl mb-1">😊</span>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Emoji</span>
                </button>
              </div>
            )}
            
            {/* Sélecteur d'emojis */}
            <AnimatePresence>
              {showSimpleAvatars && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 overflow-hidden"
                >
                  <div className={`flex flex-wrap gap-2 p-3 rounded-xl ${isDarkMode ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`}>
                    {SIMPLE_AVATARS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => {
                          setAvatar(emoji);
                          setShowSimpleAvatars(false);
                          toast.success(`${emoji} Avatar sélectionné !`);
                        }}
                        className={`w-9 h-9 rounded-full text-lg flex items-center justify-center transition-all hover:scale-110 ${
                          avatar === emoji
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 ring-2 ring-white'
                            : isDarkMode
                              ? 'bg-gray-700 hover:bg-gray-600'
                              : 'bg-white hover:bg-gray-100'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Galerie photos existantes */}
            {photos.length > 0 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden group">
                    <img src={photo.data} alt="Photo" className="w-full h-full object-cover" />
                    {photo.isMain && (
                      <div className="absolute top-0 left-0 bg-green-500 text-white text-xs px-1 rounded-br">⭐</div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <button onClick={() => handleDeletePhoto(photo.id)} className="text-white hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {/* Bouton ajouter depuis galerie */}
                {photos.length < 5 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg border-2 border-dashed flex items-center justify-center ${
                      isDarkMode ? 'border-gray-600 hover:border-pink-500' : 'border-gray-300 hover:border-pink-500'
                    }`}
                  >
                    {uploadingPhoto ? (
                      <div className="animate-spin w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full" />
                    ) : (
                      <Image className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    )}
                  </button>
                )}
              </div>
            )}
            
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
          </div>

          {/* ═══════════════════════════════════════════════════════════
              👤 SECTION 2 : QUI ES-TU ?
              ═══════════════════════════════════════════════════════════ */}
          <div 
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/70' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            onFocus={() => setGuideContext('identity')}
            onMouseEnter={() => setGuideContext('identity')}
          >
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              👤 Qui es-tu ?
            </h3>
            
            <div className="space-y-3">
              {/* Prénom + Nom sur la même ligne */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Prénom
                  </label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={customer?.firstName || "Ton prénom"}
                    className={isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Nom
                  </label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Ton nom"
                    className={isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  />
                </div>
              </div>
              
              {/* Pseudo Heartbeats */}
              <div>
                <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Pseudo Heartbeats <span className="text-gray-500">(optionnel)</span>
                </label>
                <Input
                  value={pseudo}
                  onChange={(e) => setPseudo(e.target.value.slice(0, 20))}
                  placeholder="Ex: LePetitPrince, DarkLady..."
                  className={isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {pseudo ? `@${pseudo}` : 'Un pseudo mystérieux pour te démarquer 😏'}
                </p>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              📍 SECTION 3 : LOCALISATION
              ═══════════════════════════════════════════════════════════ */}
          <div 
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/70' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            onFocus={() => setGuideContext('city')} 
            onMouseEnter={() => setGuideContext('city')}
          >
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📍 Tu habites où ?
            </h3>
            
            <select
              value={showCustomCity ? 'other' : city}
              onChange={(e) => {
                if (e.target.value === 'other') {
                  setShowCustomCity(true);
                  setCity('other');
                } else {
                  setShowCustomCity(false);
                  setCustomCity('');
                  setCity(e.target.value);
                }
              }}
              className={`w-full h-10 px-3 rounded-md border transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-purple-500`}
            >
              <option value="">Sélectionne ta ville...</option>
              <optgroup label="📍 Vermelles & alentours">
                <option value="Vermelles">Vermelles (62980)</option>
                <option value="Noyelles-lès-Vermelles">Noyelles-lès-Vermelles</option>
                <option value="Sailly-Labourse">Sailly-Labourse</option>
                <option value="Labourse">Labourse</option>
                <option value="Annequin">Annequin</option>
                <option value="Cambrin">Cambrin</option>
                <option value="Cuinchy">Cuinchy</option>
                <option value="Festubert">Festubert</option>
              </optgroup>
              <optgroup label="🏘️ Villes proches">
                <option value="Hulluch">Hulluch</option>
                <option value="Benifontaine">Benifontaine</option>
                <option value="Mazingarbe">Mazingarbe</option>
                <option value="Grenay">Grenay</option>
                <option value="Bully-les-Mines">Bully-les-Mines</option>
                <option value="Aix-Noulette">Aix-Noulette</option>
                <option value="Haisnes">Haisnes</option>
                <option value="Auchy-les-Mines">Auchy-les-Mines</option>
                <option value="Loos-en-Gohelle">Loos-en-Gohelle</option>
                <option value="Noeux-les-Mines">Nœux-les-Mines</option>
              </optgroup>
              <optgroup label="🏙️ Grandes villes du coin">
                <option value="Béthune">Béthune</option>
                <option value="Lens">Lens</option>
                <option value="Liévin">Liévin</option>
                <option value="La Bassée">La Bassée</option>
                <option value="Carvin">Carvin</option>
                <option value="Hénin-Beaumont">Hénin-Beaumont</option>
                <option value="Bruay-la-Buissière">Bruay-la-Buissière</option>
              </optgroup>
              <optgroup label="✏️ Autre">
                <option value="other">Ma ville n'est pas dans la liste...</option>
              </optgroup>
            </select>
            
            {/* Champ texte pour ville personnalisée */}
            {showCustomCity && (
              <div className="mt-2">
                <Input
                  value={customCity}
                  onChange={(e) => setCustomCity(e.target.value)}
                  placeholder="Écris le nom de ta ville..."
                  className={`${isDarkMode 
                    ? 'bg-gray-700 border-purple-500 text-white placeholder-gray-400 focus:border-pink-500 focus:ring-pink-500' 
                    : 'bg-white border-gray-300'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  ⚠️ Heartbeats privilégie les rencontres de proximité autour de Vermelles 😊
                </p>
              </div>
            )}
            
            {!showCustomCity && (
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                💜 Rencontre des gens près de chez toi !
              </p>
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════
              ✍️ SECTION 4 : BIO
              ═══════════════════════════════════════════════════════════ */}
          <div 
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/70' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            onFocus={() => setGuideContext('bio')} 
            onMouseEnter={() => setGuideContext('bio')}
          >
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              ✍️ Ta bio
            </h3>
            
            <Input
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 100))}
              placeholder="Ex: J'aime les soirées qui finissent au lever du soleil 🌅"
              className={isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
            />
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {bio.length}/100 - {100 - bio.length > 0 ? `Encore ${100 - bio.length} caractères pour briller ✨` : '💯 C\'est parfait !'}
            </p>
          </div>

          {/* Intérêts classiques (caché mais conservé pour compatibilité) */}
          <input type="hidden" value={interests} />

          {/* ═══════════════════════════════════════════════════════════
              💜 SECTION 5 : STATUT & PRÉFÉRENCES
              ═══════════════════════════════════════════════════════════ */}
          <div 
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/70' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            onFocus={() => setGuideContext('preferences')} 
            onMouseEnter={() => setGuideContext('preferences')}
          >
            <h3 className={`font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              💜 Ton statut
            </h3>
            
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(RELATIONSHIP_STATUS).map(([key, status]) => (
                <motion.button
                  key={key}
                  type="button"
                  onClick={() => setRelationshipStatus(key)}
                  className={`
                    p-3 rounded-xl border-2 transition-all text-center
                    ${relationshipStatus === key
                      ? isDarkMode
                        ? 'border-pink-500 bg-pink-500/20'
                        : 'border-pink-500 bg-pink-50'
                      : isDarkMode
                        ? 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }
                  `}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-2xl mb-1">{status.emoji}</div>
                  <div className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {status.label}
                  </div>
                </motion.button>
              ))}
            </div>
            
            {/* Options couple si sélectionné */}
            <AnimatePresence>
              {relationshipStatus !== 'single' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 pt-3"
                >
                  {relationshipStatus === 'couple' && (
                    <div>
                      <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        💕 Prénom de ton/ta partenaire
                      </label>
                      <Input
                        value={partnerName}
                        onChange={(e) => setPartnerName(e.target.value)}
                        placeholder="Ex: Julie, Thomas..."
                        className={`text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className={`block text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      🎉 Vos activités préférées ({coupleInterests.length}/6)
                    </label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {COUPLE_INTERESTS.slice(0, 8).map((interest) => {
                        const isSelected = coupleInterests.includes(interest.id);
                        return (
                          <motion.button
                            key={interest.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setCoupleInterests(coupleInterests.filter(i => i !== interest.id));
                              } else if (coupleInterests.length < 6) {
                                setCoupleInterests([...coupleInterests, interest.id]);
                              }
                            }}
                            className={`
                              p-1.5 rounded-lg border text-center transition-all
                              ${isSelected
                                ? isDarkMode
                                  ? 'border-pink-500 bg-pink-500/20'
                                  : 'border-pink-500 bg-pink-50'
                                : isDarkMode
                                  ? 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                              }
                            `}
                            whileTap={{ scale: 0.95 }}
                          >
                            <div className="text-sm">{interest.emoji}</div>
                            <div className={`text-[10px] truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {interest.label}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                      💕 Super ! On va vous aider à trouver des amis couples pour des soirées mémorables !
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Recherche - Affiché si célibataire */}
            {relationshipStatus === 'single' && (
              <div className="mt-4 pt-4 border-t border-gray-600/30">
                <h4 className={`font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  💘 Je souhaite rencontrer...
                </h4>
                <div className="flex gap-2">
                  {[
                    { value: 'women', label: '👩 Des femmes' },
                    { value: 'men', label: '👨 Des hommes' },
                    { value: 'all', label: '💜 Les deux' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setLookingFor(option.value)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        lookingFor === option.value
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                          : isDarkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  🔒 Tu ne seras visible que par les personnes qui correspondent à tes préférences
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
          >
            <Heart className="w-5 h-5 mr-2" />
            {existingProfile ? 'Enregistrer' : 'Activer mon profil 💜'}
          </Button>
        </div>
      </DialogContent>

      {/* Modal Créateur d'Avatar Magique */}
      <Dialog open={showAvatarCreator} onOpenChange={setShowAvatarCreator}>
        <DialogContent className={`max-w-lg max-h-[90vh] overflow-y-auto p-0 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white'}`}>
          <AvatarMagiqueCreator
            customerId={customerId}
            onSave={(avatarData) => {
              // avatarData contient { avatarImage, avatarId, style }
              if (avatarData?.avatarImage) {
                setAvatar(avatarData.avatarImage);
              }
              setShowAvatarCreator(false);
            }}
            onClose={() => setShowAvatarCreator(false)}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default ProfileSetupModal;
